import {
	type Bordereau,
	type Exercice,
	type Mouvement,
	Sens,
} from '@mairistem/comptabilite';
import {
	FilterScope,
	getErrorMessage,
	type UserRole,
} from '@mairistem/finances-utils';
import { newIdentifiant } from '@mairistem/tools';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import BordereauSection from '../../components/Liquidation/LiquidationTreeView/Bordereau/BordereauSection';
import EntiteSection from '../../components/Liquidation/LiquidationTreeView/Entite/EntiteSection';
import CreateMouvementRow from '../../components/Liquidation/LiquidationTreeView/Mouvement/CreateMouvementRow';
import MouvementActionIcon from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementActionIcon';
import MouvementExpanded from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementExpanded';
import MouvementReduce from '../../components/Liquidation/LiquidationTreeView/Mouvement/MouvementReduce';
import BordereauEtat from '../../constants/liquidation/bordereaux/BordereauEtat';
import liquidationHeight from '../../constants/liquidation/LiquidationHeight';
import TreeNodeType from '../../constants/liquidation/TreeNodeType';
import CODE_BHM from '../../constants/mouvements/codeBHM';
import { TYPE_BORDEREAU } from '../../constants/mouvements/typeBordereau';
import type {
	BordereauWithState,
	TreeRow,
} from '../../interfaces/pieces/TreeRow';
import { getBordereaux } from '../../services/piecesComptables/bordereaux/bordereaux';
import getMouvements from '../../services/piecesComptables/mouvements/mouvements';
import BordereauNouveauState from '../bordereaux/state/BordereauNouveauState';
import BordereauStateFactory from '../bordereaux/state/BordereauStateFactory';
import BaseTreeView from '../pieces/BaseTreeView';
import TreeDataManager from '../pieces/TreeDataManager';

interface DefaultNewBordereauCommonData {
	codeBHM: string;
	identifiantEntite: number;
	identifiantExercice: number;
}

type MouvementExtended = Mouvement & {
	isAnotherBordereau?: boolean;
	isNewBordereau?: boolean;
};

class MouvementTreeView extends BaseTreeView {
	orderData(data: TreeRow[], nodeType: TreeNodeType): TreeRow[] {
		switch (nodeType) {
			case TreeNodeType.ENTITE:
				return data?.sort(this.orderBordereaux);
			case TreeNodeType.BORDEREAU:
				return data?.sort(this.orderMouvements);
			default:
				return data;
		}
	}

	loadData = async (rowData: TreeRow): Promise<TreeRow[] | null> => {
		let data: TreeRow[] = null;

		switch (rowData.nodeType) {
			case TreeNodeType.ENTITE:
				data = await this.loadBordereaux(rowData);
				break;
			case TreeNodeType.BORDEREAU:
				data = await this.loadMouvements(rowData);
				break;
			default:
				return null;
		}

		return this.orderData(data, rowData.nodeType);
	};

	getSensOrder = (row: TreeRow) => {
		switch (row?.bordereau?.sens) {
			case Sens.DEPENSE:
				return 3;
			case Sens.RECETTE:
				return 2;
			default:
				return 1;
		}
	};

	orderBordereaux = (a: TreeRow, b: TreeRow): number => {
		const sensA: number = this.getSensOrder(a);
		const sensB: number = this.getSensOrder(b);

		if (sensA !== sensB) {
			return sensB - sensA;
		}

		const stepA: number = a.bordereau.state.step ?? 0;
		const stepB: number = b.bordereau.state.step ?? 0;

		if (stepA !== stepB) {
			return stepA - stepB;
		}

		const numeroA: number = Number(a?.bordereau?.numero) || 0;
		const numeroB: number = Number(b?.bordereau?.numero) || 0;

		return numeroB - numeroA;
	};

	orderMouvements = (a: TreeRow, b: TreeRow): number => {
		const numeroA: number = Number(a?.mouvement?.numero) || 0;
		const numeroB: number = Number(b?.mouvement?.numero) || 0;

		return numeroB - numeroA;
	};

	loadBordereaux = async (entiteRow: TreeRow): Promise<TreeRow[]> => {
		try {
			const { data: bordereaux } = await getBordereaux(
				new URLSearchParams({
					all: 'true',
					codeBHM: FilterScope.filterIn([
						CODE_BHM.VALUE.BROUILLARD,
						CODE_BHM.VALUE.HISTORISE,
					]),
					identifiantEntite: entiteRow.identifiant.toString(),
					identifiantExercice: entiteRow.exercice.identifiant.toString(),
					journalEtat: BordereauEtat.TRAITEMENT_PES_EN_INSTANCE,
					withJournaux: 'true',
					withMontant: 'true',
					withPiecesCount: 'true',
					filterOnDate: 'true',
				}),
			);

			const bordereauList: TreeRow[] = [];
			const libelleBordereau: string = 'Bordereau de travail';
			const entiteId = entiteRow.identifiant;

			const defaultNewBordereauData: DefaultNewBordereauCommonData = {
				codeBHM: CODE_BHM.VALUE.BROUILLARD,
				identifiantEntite: this.getExercice(entiteRow)?.identifiant,
				identifiantExercice: this.getExercice(entiteRow)?.identifiantEntite,
			};

			// Ajout des bordereaux de type "Nouveau" par défaut
			for (let i = 0; i < 4; i++) {
				const isAnnulation = i % 2;
				const isMandat = i < 2;

				bordereauList.push({
					identifiant: newIdentifiant(),
					libelle: libelleBordereau,
					bordereau: {
						identifiant: newIdentifiant(),
						sens: isMandat ? Sens.DEPENSE : Sens.RECETTE,
						libelle: libelleBordereau,
						state: new BordereauNouveauState(),
						...(isAnnulation && {
							typePes: isMandat
								? TYPE_BORDEREAU.DEPENSE.ANNUL_REDUC
								: TYPE_BORDEREAU.RECETTE.ANNUL_REDUC,
						}),
						...defaultNewBordereauData,
					},
					rowKey: `bordereau-new-${isAnnulation ? 'annulation-' : ''}${isMandat ? 'depense' : 'recette'}-${entiteId}`,
					children: [
						{
							identifiant: newIdentifiant(),
							libelle: `Créer un ${isMandat ? 'mandat' : 'titre'}`,
							rowKey: `mouvement-create-${isAnnulation ? 'annulation-' : ''}${isMandat ? 'mandat' : 'titre'}-${entiteId}`,
							nodeType: TreeNodeType.CREATE_MOUVEMENT,
						},
					],
					nodeType: TreeNodeType.CREATE_BORDEREAU,
				});
			}

			const { exercice } = entiteRow;
			const resultBordereau = bordereaux.map((bordereau: Bordereau) => ({
				identifiant: bordereau.identifiant,
				libelle: bordereau.libelle,
				bordereau: {
					...bordereau,
					montant: bordereau.montant ?? '0',
					state: BordereauStateFactory.getState(bordereau, exercice),
				},
				rowKey: `bordereau-${bordereau.identifiant}`,
				children: [],
				nodeType: TreeNodeType.BORDEREAU,
			}));

			resultBordereau.forEach((bordereau: TreeRow) => {
				bordereauList.push(bordereau);
			});

			return bordereauList;
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des bordereaux'),
			);
			return [];
		}
	};

	loadMouvements = async (bordereauRow: TreeRow): Promise<TreeRow[]> => {
		try {
			const firstPageResponse = await getMouvements(
				new URLSearchParams({
					identifiantBordereau: bordereauRow.identifiant as string,
					page: '1',
				}),
			);

			const { data: firstPageMouvements, meta } = firstPageResponse;

			const totalPages = meta?.lastPage ?? 1;

			const pagePromises: Promise<any>[] = [];

			for (let page = 2; page <= totalPages; page++) {
				pagePromises.push(
					getMouvements(
						new URLSearchParams({
							identifiantBordereau: bordereauRow.identifiant as string,
							page: page.toString(),
						}),
					),
				);
			}

			const otherPages = await Promise.all(pagePromises);
			const allOtherMouvements = otherPages.flatMap((res) => res.data);

			const allMouvements = [...firstPageMouvements, ...allOtherMouvements];

			let createRow: TreeRow = null;
			if (bordereauRow?.bordereau?.codeBHM === CODE_BHM.VALUE.BROUILLARD) {
				createRow = {
					identifiant: bordereauRow.identifiant,
					libelle: `Créer un ${bordereauRow.bordereau.sens === Sens.DEPENSE ? 'mandat' : 'titre'}`,
					rowKey: `mouvement-create-${bordereauRow.identifiant}`,
					nodeType: TreeNodeType.CREATE_MOUVEMENT,
				};
			}

			return [
				...(!isNil(createRow) ? [createRow] : []),
				...allMouvements.map((mouvement: Mouvement) => ({
					identifiant: mouvement.identifiant as number,
					libelle: mouvement.objetPiece,
					mouvement,
					rowKey: `mouvement-${mouvement.identifiant}`,
					children: null,
					nodeType: TreeNodeType.MOUVEMENT,
				})),
			];
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des mouvements'),
			);
			return [];
		}
	};

	getRowHeight = (rowData: TreeRow, expanded: boolean): number => {
		if (!rowData?.nodeType) return liquidationHeight.DEFAULT;

		switch (rowData.nodeType) {
			case TreeNodeType.MOUVEMENT:
				if (expanded) {
					return liquidationHeight.MOUVEMENT_EXPANDED;
				}
				return liquidationHeight.MOUVEMENT_REDUCE;
			case TreeNodeType.CREATE_BORDEREAU:
			case TreeNodeType.BORDEREAU:
				return liquidationHeight.BORDEREAU;
			case TreeNodeType.CREATE_MOUVEMENT:
				return liquidationHeight.CREATE_MOUVEMENT;
			default:
				return liquidationHeight.DEFAULT;
		}
	};

	renderRow = (
		rowData: TreeRow,
		expanded: boolean,
		userRole: UserRole,
		onClick: VoidFunction,
		onDoubleClick: VoidFunction,
		onChange: (
			row: TreeRow,
			shouldRefreshChildren?: boolean,
			newParent?: TreeRow,
		) => void,
		onDelete?: (row: TreeRow) => void,
		onAdd?: (row: TreeRow) => void,
		options?: { filterSens?: string; data?: TreeRow[] },
	) => {
		const { data } = options ?? {};
		const exercice: Exercice = this.getExercice(rowData, data);

		switch (rowData.nodeType) {
			case TreeNodeType.MOUVEMENT: {
				let bordereauRow: TreeRow = TreeDataManager.getParentFromData(
					data,
					rowData.rowKey,
				);
				let updatedBordereau: BordereauWithState = bordereauRow?.bordereau;
				const activeRowData = rowData;

				const onAddMouvement = (mouvement: MouvementExtended) => {
					// On ajoute le mouvement sur un autre bordereau
					const isAnotherBordereau =
						mouvement.isAnotherBordereau || mouvement.isNewBordereau;

					if (isAnotherBordereau) {
						let entiteRow: TreeRow = TreeDataManager.getParentFromData(
							data,
							bordereauRow?.rowKey,
						);

						if (mouvement.isNewBordereau) {
							// J'ajoute d'abord le mouvement à la row du bordereau
							bordereauRow = this.handleAddMouvement(bordereauRow, mouvement);
							// J'ajoute ensuite la row du bordereau sur la row de l'entité
							entiteRow = this.handleAddBordereau(
								bordereauRow,
								{ ...mouvement.bordereau, montant: String(mouvement.montant) },
								data,
							);
							// On modifie la row de l'entité dans les vraies data
							onAdd(entiteRow);

							return;
						}

						// On trouve le bordereau de destination
						bordereauRow = entiteRow?.children?.find(
							(b) => b.identifiant === mouvement.identifiantBordereau,
						);
						updatedBordereau = bordereauRow?.bordereau;
					}

					const newBordereauMontant =
						Number(updatedBordereau?.montant ?? 0) + mouvement.montant;

					bordereauRow = this.handleAddMouvement(bordereauRow, mouvement);
					onAdd(bordereauRow);

					onChange(
						{
							...activeRowData,
							bordereau: {
								...updatedBordereau,
								montant: String(newBordereauMontant),
							},
						},
						isAnotherBordereau,
						{
							...bordereauRow,
							bordereau: {
								...bordereauRow.bordereau,
								montant: String(newBordereauMontant),
							},
						},
					);
				};

				return (
					<div className="mouvement-row">
						{expanded ? (
							<MouvementExpanded
								rowData={rowData}
								onClick={onClick}
								onDoubleClick={onDoubleClick}
								exercice={exercice}
							/>
						) : (
							<MouvementReduce
								rowData={rowData}
								onClick={onClick}
								onDoubleClick={onDoubleClick}
								exercice={exercice}
							/>
						)}
						<MouvementActionIcon
							exercice={exercice}
							bordereau={updatedBordereau}
							mouvement={rowData.mouvement}
							alwaysVisible={expanded}
							userRole={userRole}
							onChange={(mouvement) => onChange({ ...rowData, mouvement })}
							onAdd={onAddMouvement}
							onDelete={(mouvement) => onDelete?.({ ...rowData, mouvement })}
						/>
					</div>
				);
			}
			case TreeNodeType.CREATE_MOUVEMENT:
				return <CreateMouvementRow rowData={rowData} exercice={exercice} />;
			case TreeNodeType.ENTITE:
				return <EntiteSection entite={rowData.exercice?.entite} />;
			case TreeNodeType.BORDEREAU:
			case TreeNodeType.CREATE_BORDEREAU:
				return (
					<BordereauSection
						rowData={rowData}
						exercice={exercice}
						expanded={expanded}
						onChange={(bordereau, shouldRefreshChildren) =>
							onChange({ ...rowData, bordereau }, shouldRefreshChildren)
						}
						onDelete={(bordereau) => onDelete?.({ ...rowData, bordereau })}
						filterSens={options?.filterSens as string}
						onAdd={(bordereau) =>
							onAdd(this.handleAddBordereau(rowData, bordereau, data))
						}
						userRole={userRole}
					/>
				);
			default:
				return null;
		}
	};

	isRowExpandable = (rowData: TreeRow): boolean =>
		rowData.nodeType === TreeNodeType.MOUVEMENT;

	handleAddBordereau = (
		parentRow: TreeRow,
		bordereau: BordereauWithState,
		data?: TreeRow[],
	): TreeRow => {
		const exercice: Exercice = this.getExercice(parentRow, data);
		const state = BordereauStateFactory.getState(
			bordereau as Bordereau,
			exercice,
		);

		const newTreeRowBordereau: TreeRow = {
			bordereau: {
				...bordereau,
				montant: bordereau.montant ?? '0',
				state,
			},
			children: [],
			rowKey: `bordereau-${bordereau.identifiant}`,
			libelle: bordereau.libelle,
			nodeType: TreeNodeType.BORDEREAU,
			identifiant: bordereau.identifiant,
		};
		const entiteRow: TreeRow = TreeDataManager.getParentFromData(
			data,
			parentRow?.rowKey,
		);

		return this.handleAddRow(entiteRow, newTreeRowBordereau);
	};

	handleAddMouvement = (parentRow: TreeRow, mouvement: Mouvement): TreeRow => {
		const newTreeRowMouvement: TreeRow = {
			mouvement,
			rowKey: `mouvement-${mouvement.identifiant}`,
			libelle: mouvement.objetPiece,
			nodeType: TreeNodeType.MOUVEMENT,
			identifiant: mouvement.identifiant,
		};

		return this.handleAddRow(parentRow, newTreeRowMouvement);
	};
}

export default MouvementTreeView;
